import { getClient } from '@sakura-grp/database';

export interface StoreOption {
  id: string;
  store_name: string;
  short_name: string;
}

export interface AssignedStore extends StoreOption {
  is_current: boolean;
  assigned_from: string;
  assigned_to: string | null;
}

/**
 * 店舗一覧を取得する
 * @returns 店舗リスト（フィルター表示用）
 */
export async function fetchStores(): Promise<StoreOption[]> {
  const supabase = getClient();

  const { data: stores, error } = await supabase
    .from('stores')
    .select('id, store_name, short_name')
    .is('deleted_at', null)
    .eq('is_visible', true)
    .order('no', { ascending: true });

  if (error) {
    console.error('[fetchStores] エラー:', error);
    return [];
  }

  return (stores || []).map((store) => ({
    id: store.id,
    store_name: store.store_name || '',
    short_name: store.short_name || store.store_name?.slice(0, 2) || '',
  }));
}

type StoreRelation = { id: string; store_name: string; short_name: string } | null;

function toAssignedStore(
  store: StoreRelation,
  from: string,
  to: string | null,
  isCurrent: boolean,
): AssignedStore | null {
  if (!store) return null;
  return {
    id: store.id,
    store_name: store.store_name || '',
    short_name: store.short_name || store.store_name?.slice(0, 2) || '',
    is_current: isCurrent,
    assigned_from: from,
    assigned_to: to,
  };
}

/**
 * ホステスの関連店舗を取得する（正規所属 + ヘルプシフト、過去+現在）
 *
 * ヘルプシフトで発生した名刺・予約・営業活動も自分のデータなので、
 * ダッシュボードの店舗フィルタに含める必要がある。
 *
 * @param hostessId - ホステスID
 * @returns 関連店舗リスト（正規所属とヘルプシフトの両方を統合、重複排除済み）
 */
export async function fetchAssignedStores(hostessId: string): Promise<AssignedStore[]> {
  const supabase = getClient();

  const [assignmentsResult, helpShiftsResult] = await Promise.all([
    supabase
      .from('hostess_store_assignments')
      .select(`
        store_id,
        assigned_from,
        assigned_to,
        store:stores(id, store_name, short_name)
      `)
      .eq('hostess_id', hostessId)
      .order('assigned_from', { ascending: false }),
    supabase
      .from('hostess_help_shifts')
      .select(`
        store_id,
        shift_from,
        shift_to,
        store:stores(id, store_name, short_name)
      `)
      .eq('hostess_id', hostessId)
      .is('cancelled_at', null)
      .order('shift_from', { ascending: false }),
  ]);

  if (assignmentsResult.error) {
    console.error('[fetchAssignedStores] hostess_store_assignments エラー:', assignmentsResult.error);
  }
  if (helpShiftsResult.error) {
    console.error('[fetchAssignedStores] hostess_help_shifts エラー:', helpShiftsResult.error);
  }

  const storeMap = new Map<string, AssignedStore>();

  // 1. 正規所属（現在所属を優先）
  for (const assignment of assignmentsResult.data || []) {
    const entry = toAssignedStore(
      assignment.store as StoreRelation,
      assignment.assigned_from,
      assignment.assigned_to,
      assignment.assigned_to === null,
    );
    if (!entry) continue;

    const existing = storeMap.get(entry.id);
    if (!existing || (entry.is_current && !existing.is_current)) {
      storeMap.set(entry.id, entry);
    }
  }

  // 2. ヘルプシフト（正規所属がない店舗のみ追加）
  const now = Date.now();
  for (const helpShift of helpShiftsResult.data || []) {
    const store = helpShift.store as StoreRelation;
    if (!store) continue;

    // 正規所属で既に登録されていれば上書きしない
    if (storeMap.has(store.id)) continue;

    const shiftFromMs = new Date(helpShift.shift_from).getTime();
    const shiftToMs = new Date(helpShift.shift_to).getTime();
    const isCurrent = shiftFromMs <= now && now <= shiftToMs;

    const entry = toAssignedStore(store, helpShift.shift_from, helpShift.shift_to, isCurrent);
    if (entry) {
      storeMap.set(store.id, entry);
    }
  }

  // 現在所属を先頭に、その後は assigned_from の降順
  return Array.from(storeMap.values()).sort((a, b) => {
    if (a.is_current && !b.is_current) return -1;
    if (!a.is_current && b.is_current) return 1;
    return new Date(b.assigned_from).getTime() - new Date(a.assigned_from).getTime();
  });
}
